import React, { useState, useEffect, useRef } from 'react';
import { Navbar, NavbarBrand, NavbarCollapse, NavbarLink, NavbarToggle } from "flowbite-react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Logo from "../brand/Logo";
import ThemeToggle from "./ThemeToggle";
import LanguageToggle from "./LanguageToggle";

const PLAY_STORE_URL = "https://play.google.com/store/apps/details?id=com.emmpieza.tunegocioonline";
const CURSOS_URL = "https://elretornodelospolimatas.emmpieza.com/";
//const WEBPAGES_URL = "https://webpages.emmpieza.com/";

const moreLinks = [
    { labelKey: "nav.blog", to: "/blog" },
    { labelKey: "nav.servicios", to: "/", hash: "servicios" },
    { labelKey: "nav.contacto", to: "/", hash: "contacto" },
    // { labelKey: "nav.webpages", to: WEBPAGES_URL, external: true },
];

function NavbarComponent() {
    const { t } = useTranslation();
    const [isScrolled, setIsScrolled] = useState(false);
    const [isDeckPastHero, setIsDeckPastHero] = useState(false);
    const [moreOpen, setMoreOpen] = useState(false);
    const moreRef = useRef(null);
    const location = useLocation();

    const isActive = (path) => location.pathname === path || (path !== '/' && location.pathname.startsWith(path));
    const isHashActive = (hash) => location.pathname === '/' && location.hash === `#${hash}`;
    const isMoreActive = moreLinks.some((l) => !l.external && (l.hash ? isHashActive(l.hash) : isActive(l.to)));

    useEffect(() => {
        if (!moreOpen) return;
        const handleClickOutside = (e) => {
            if (moreRef.current && !moreRef.current.contains(e.target)) setMoreOpen(false);
        };
        const handleEscape = (e) => { if (e.key === "Escape") setMoreOpen(false); };
        document.addEventListener("mousedown", handleClickOutside);
        document.addEventListener("keydown", handleEscape);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
            document.removeEventListener("keydown", handleEscape);
        };
    }, [moreOpen]);

    useEffect(() => { setMoreOpen(false); }, [location.pathname]);

    useEffect(() => {
        const readDeckSlide = () => {
            const dots = document.querySelectorAll(".deck-dot");
            if (!dots.length) {
                setIsDeckPastHero(false);
                return;
            }
            const activeIndex = [...dots].findIndex((dot) =>
                dot.classList.contains("deck-dot--active"),
            );
            setIsDeckPastHero(activeIndex > 0);
        };

        const handleScroll = () => {
            const deckInner = document.querySelector(".deck-slide-inner");
            const innerScrolled = deckInner ? deckInner.scrollTop > 12 : false;
            setIsScrolled(window.scrollY > 12 || innerScrolled);
            readDeckSlide();
        };

        readDeckSlide();
        handleScroll();

        const deckDots = document.querySelector(".deck-dots");
        const deckObserver = deckDots
            ? new MutationObserver(handleScroll)
            : null;
        deckObserver?.observe(deckDots, {
            attributes: true,
            subtree: true,
            attributeFilter: ["class"],
        });

        window.addEventListener("scroll", handleScroll, { passive: true });
        document.addEventListener("scroll", handleScroll, { passive: true, capture: true });

        return () => {
            deckObserver?.disconnect();
            window.removeEventListener("scroll", handleScroll);
            document.removeEventListener("scroll", handleScroll, true);
        };
    }, [location.pathname]);

    const isHomeHero =
        location.pathname === "/" && !isDeckPastHero && !isScrolled;
    const shellClass = [
        "navbar-shell",
        isScrolled || isDeckPastHero ? "navbar-shell--scrolled" : "",
        isHomeHero ? "navbar-shell--hero" : "navbar-shell--solid",
    ]
        .filter(Boolean)
        .join(" ");

    const linkClass = (active) =>
        `font-bold transition-all duration-300 ${isScrolled || isDeckPastHero ? 'text-sm md:text-base' : 'text-xl'} ${active ? '!text-em-accent' : '!text-em-text hover:!text-em-accent'}`;

    return (
        <div className={shellClass}>
            <style>{`
                @media (max-width: 768px) {
                    div[data-testid="flowbite-navbar-collapse"]:not(.hidden) {
                        animation: menuDrop 0.35s cubic-bezier(0.16, 1, 0.3, 1) forwards;
                        transform-origin: top;
                    }
                }
                @keyframes menuDrop {
                    0% { opacity: 0; transform: translateY(-10px) scaleY(0.98); }
                    100% { opacity: 1; transform: translateY(0) scaleY(1); }
                }
            `}</style>
            <Navbar className="!bg-transparent dark:!bg-transparent max-w-[1200px] mx-auto w-full px-4 transition-all duration-300">
                <NavbarBrand as={Link} to="/" className="shrink-0 gap-3 text-em-text">
                    <Logo
                        className={`w-auto transition-all duration-300 ${isScrolled || isDeckPastHero ? 'h-10 sm:h-12' : 'h-12 max-w-[min(100vw-4rem,280px)] sm:h-14'}`}
                    />
                    <span className="sr-only">{t("common.brandSr")}</span>
                </NavbarBrand>
                <div className="flex items-center gap-2 md:order-2">
                    <a
                        href={PLAY_STORE_URL}
                        aria-label={t("common.playStoreAria")}
                        title={t("common.playStoreAria")}
                        className="flex h-9 w-9 items-center justify-center rounded-full text-em-text/80 transition-colors hover:bg-em-text/5 hover:text-em-accent"
                    >
                        <svg viewBox="0 0 24 24" className="h-5 w-5" fill="currentColor" aria-hidden="true">
                            <path d="M3.6 2.6c-.35.35-.55.85-.55 1.45v15.9c0 .6.2 1.1.55 1.45l.1.09L13 12.2v-.4L3.7 2.5l-.1.1Z" />
                            <path d="M16.2 15.4 13 12.2v-.4l3.2-3.2 .07.04 3.79 2.15c1.08.62 1.08 1.63 0 2.25l-3.79 2.15-.07.05Z" />
                            <path d="M16.27 15.36 13 12.1 3.7 21.4c.35.37.93.42 1.58.05l10.99-6.09" />
                            <path d="M16.27 8.64 5.28 2.55c-.65-.37-1.23-.32-1.58.05L13 12l3.27-3.36Z" />
                        </svg>
                    </a>
                    <ThemeToggle />
                    <LanguageToggle />
                    <NavbarToggle className="!text-em-text hover:!bg-em-text/5" />
                </div>
                <NavbarCollapse className="navbar-mobile-menu mt-4 md:mt-0 p-4 md:p-0 rounded-lg md:bg-transparent md:shadow-none md:border-none md:backdrop-blur-none">
                    <NavbarLink as={Link} to="/" className={linkClass(location.pathname === '/')}>
                        {t("nav.home")}
                    </NavbarLink>
                    <NavbarLink as={Link} to="/Proyectos" className={linkClass(isActive('/Proyectos'))}>{t("nav.proyectos")}</NavbarLink>
                    <NavbarLink as={Link} to="/ia-aviacion-seguridad-operacional" className={linkClass(isActive('/ia-aviacion-seguridad-operacional'))}>
                        {t("nav.iaAviacion")}
                    </NavbarLink>
                    <NavbarLink as="a" href={CURSOS_URL} className={linkClass(false)}>
                        {t("nav.cursos")}
                    </NavbarLink>
                    <NavbarLink as={Link} to="/analiza-tu-cv" className={linkClass(isActive("/analiza-tu-cv"))}>
                        {t("nav.analizaCv")}
                    </NavbarLink>
                    <li ref={moreRef} className="relative list-none">
                        <button
                            type="button"
                            onClick={() => setMoreOpen((v) => !v)}
                            aria-expanded={moreOpen}
                            className={`flex items-center gap-1 ${linkClass(isMoreActive)}`}
                        >
                            {t("nav.more")}
                            <svg
                                viewBox="0 0 20 20"
                                className={`h-3.5 w-3.5 transition-transform duration-200 ${moreOpen ? 'rotate-180' : ''}`}
                                fill="currentColor"
                                aria-hidden="true"
                            >
                                <path d="M5.5 7.5 10 12l4.5-4.5" stroke="currentColor" strokeWidth="1.6" fill="none" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                        </button>
                        <div
                            className={`mt-2 flex flex-col gap-1 rounded-lg border border-em-text/10 bg-em-bg p-2 shadow-lg md:absolute md:right-0 md:mt-2 md:min-w-[220px] ${moreOpen ? 'flex' : 'hidden'}`}
                        >
                            {moreLinks.map(({ labelKey, to, hash, external }) =>
                                external ? (
                                    <a
                                        key={labelKey}
                                        href={to}
                                        onClick={() => setMoreOpen(false)}
                                        className="rounded-md px-3 py-2 text-sm font-semibold text-em-text transition-colors hover:bg-em-text/5 hover:text-em-accent"
                                    >
                                        {t(labelKey)}
                                    </a>
                                ) : (
                                    <Link
                                        key={labelKey}
                                        to={hash ? { pathname: to, hash } : to}
                                        onClick={() => setMoreOpen(false)}
                                        className={`rounded-md px-3 py-2 text-sm font-semibold transition-colors hover:bg-em-text/5 hover:text-em-accent ${(hash ? isHashActive(hash) : isActive(to)) ? 'text-em-accent' : 'text-em-text'}`}
                                    >
                                        {t(labelKey)}
                                    </Link>
                                )
                            )}
                        </div>
                    </li>
                </NavbarCollapse>
            </Navbar>
        </div>
    );
}

export default NavbarComponent;
